import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'

countries.registerLocale(enLocale)

/**
 * The tax ID types people pick from, as Stripe names them: a country prefix,
 * then what the number is. The API accepts any type of that shape and Stripe
 * Tax is what refuses one it doesn't know, so this is the common ones, not a
 * list the API enforces.
 */
export const TAX_ID_TYPES = [
    { value: 'eu_vat', label: 'EU VAT' },
    { value: 'gb_vat', label: 'UK VAT' },
    { value: 'us_ein', label: 'US EIN' },
    { value: 'ca_gst_hst', label: 'Canada GST/HST' },
    { value: 'au_abn', label: 'Australia ABN' },
    { value: 'nz_gst', label: 'New Zealand GST' },
    { value: 'in_gst', label: 'India GST' },
    { value: 'sg_gst', label: 'Singapore GST' },
    { value: 'ch_vat', label: 'Switzerland VAT' },
    { value: 'no_vat', label: 'Norway VAT' },
    { value: 'za_vat', label: 'South Africa VAT' },
    { value: 'br_cnpj', label: 'Brazil CNPJ' },
    { value: 'mx_rfc', label: 'Mexico RFC' },
]

/**
 * A tax ID as one line, e.g. "EU VAT DE123456789".
 *
 * @param {string|null} type - Stripe tax ID type
 * @param {string|null} number
 * @returns {string|null}
 */
export function taxIdLabel(type, number) {
    if (!number) return null

    const known = TAX_ID_TYPES.find((item) => item.value === type)
    const label =
        known?.label ??
        (type ? type.toUpperCase().replace(/_/g, ' ') : 'Tax ID')

    return `${label} ${number}`
}

/**
 * Every country, for the picker: ISO alpha-2 code and English name, by name.
 *
 * @returns {Array<{ code: string, name: string }>}
 */
export function countryOptions() {
    return Object.entries(countries.getNames('en', { select: 'official' }))
        .map(([code, name]) => ({ code, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * A country's name from its ISO code. The API stores the code; a value that
 * isn't one (an older account may hold a name) is shown as it is.
 *
 * @param {string} value
 * @returns {string}
 */
export function countryName(value) {
    if (!value) return ''

    return countries.getName(value.toUpperCase(), 'en') ?? value
}

/**
 * The ISO code for a country the account holds as either a code or a name, or
 * an empty string when it is neither.
 *
 * @param {string|null} value
 * @returns {string}
 */
export function countryCode(value) {
    if (!value) return ''

    const upper = value.toUpperCase()
    if (countries.isValid(upper)) return upper

    return countries.getAlpha2Code(value, 'en') ?? ''
}
